import AddIcon from "@mui/icons-material/Add";
import { Box, Stack, Typography } from "@mui/material";
import React, { memo, useContext, useEffect, useState } from "react";
import GlobalButton from "../../../components/button";
import CustomCheckBox from "../../../components/checkbox";
import AppLoader from "../../../components/loader";
import { AppContext } from "../../../lib/Context";
import { getBroadbandForWeb } from "../../../services/BroadbandApi";
import { handleOpenSnackBar, isBuyPricingContract } from "../../../utils/helper";
import useCliManagement from "./../../../components/hooks/ConnectionBoxFn";
import useCliLineManagement from "./../../../components/hooks/ConnectionLineFn";
import ConnectionBox from "./ConnectionBox";
import ConnectionLines from "./ConnectionLines";
import { useLocation } from "react-router-dom";

const Cli = () => {
  const location = useLocation();
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [legacyTarrifService, setLegacyTarrifService] = useState([]);
  const [digitalTarrifService, setDigitalTarrifService] = useState([]);
  const [digitalNgnTarrifOption, setdigitalNgnTarrifOption] = useState([]);
  const [legacyTarifOptions, setLegacyTarifOptions] = useState([]);

  const { cliData, setCliData, contractLength, userData } =
    useContext(AppContext);
  const isDetailPage = location.pathname === "/prospect-details";

  const extractUniqueServices = (data) => {
    let services = new Set();

    data.forEach((item) => {
      item.lineValues.forEach((line) => {
        // Add Legacy Value if exists
        if (line.legacy) {
          services.add(line.legacy.value);
        }

        // Add Legacy Services if exist
        for (let i = 1; i <= 4; i++) {
          let service = line[`legacyService${i}`];
          if (service) {
            services.add(service.value);
          }
        }

        // Add NGN Value if exists
        if (line.ngnValue) {
          services.add(line.ngnValue.value);
        }

        // Add NGN Services if exist
        for (let i = 1; i <= 4; i++) {
          let service = line[`ngnService${i}`];
          if (service) {
            services.add(service.value);
          }
        }
      });
    });

    return [...services];
  };

  // Example Usage
  const uniqueServices = extractUniqueServices(cliData);

  const {
    handleChangeLineValue,
    handleDisableLine,
    handleChangeCliNumber,
    handleChangeAddress,
    handeDeleteCliBox,
    handlAddCliBox,
    handleCheckedOptions,
    handleChangeUpgradeCharge,
    handleSelectUpgradeAttachedValue,
    handleUpdateNumber,
    handleSelectTimePeriod,
    handleSelectPaymentWauy,
    handleUpdateSaleType
  } = useCliManagement(cliData, setCliData);

  const {
    handleChangeLegacyValue,
    handleChangeLegacyService,
    handleChangeNgnValue,
    handleChangeNgnService,
  } = useCliLineManagement(cliData, setCliData, contractLength);

  const handleEditLegacySalePrice = (
    mainIndex,
    lineIndex,
    value,
    item,
    key,
    isCopyValue
  ) => {
    const selectedContract = contractLength.find(
      (item) => item.isSelected === true
    );
    const selectedLength = selectedContract?.value;
    const isBuyMode = isBuyPricingContract(contractLength);

    if (selectedLength === undefined) {
      return handleOpenSnackBar("error", "Kindly select contract length");
    }

    const selectedTarrif = item[key];
    const numericValue = parseFloat(value);

    // Create a deep copy of cliData
    let tempArray = JSON.parse(JSON.stringify(cliData));

    let lineValue = tempArray[mainIndex].lineValues[lineIndex][key];

    lineValue.error = false;
    lineValue.errorMessage = "";

    const lengthValidation = {
      12: selectedTarrif.length12,
      18: selectedTarrif.length18,
      24: selectedTarrif.length24,
      36: selectedTarrif.length36,
    };

    // Allow clearing the field while typing (backspace)
    if (value === "" || value === null || value === undefined) {
      lineValue.salePrice = "";
      lineValue.error = false;
      lineValue.errorMessage = "";
    } else if (numericValue < 0) {
      lineValue.error = true;
      lineValue.errorMessage = "Value cannot be less than 0";
    } else if (isBuyMode) {
      // Buy mode: max = length36; below buyPrice allowed with error (same pattern as sale mode)
      const minPrice = selectedTarrif.buyPrice;
      const maxPrice = selectedTarrif.length36;

      if (numericValue > maxPrice) {
        lineValue.error = true;
        lineValue.errorMessage = `Price cannot be greater than ${maxPrice}`;
      } else if (numericValue < minPrice) {
        lineValue.salePrice = numericValue;
        lineValue.error = true;
        lineValue.errorMessage = `You cannot go below buy price (${minPrice})`;
      } else {
        lineValue.salePrice = numericValue;
      }
    } else if (numericValue > selectedTarrif.highPrice) {
      lineValue.error = true;
      lineValue.errorMessage = `Sale price cannot be greater than ${selectedTarrif.highPrice}`;
    } else if (numericValue < lengthValidation[selectedLength]) {
      lineValue.salePrice = numericValue;
      lineValue.error = true;
      lineValue.errorMessage = `For Contract length ${selectedLength}, sale price cannot be less than ${lengthValidation[selectedLength]}`;
    } else {
      lineValue.salePrice = numericValue;
    }

    // Update the entire lineValues array using map
    if (isCopyValue) {
      tempArray[mainIndex].lineValues = tempArray[mainIndex].lineValues.map(
        (val) => {
          return {
            ...val,
            [key]: lineValue,
          };
        }
      );
    }

    setCliData(tempArray);
  };

  const renderCliOptions = (mainIndex, option, cliNumber) => (
    <Stack
      direction={"row"}
      spacing={1}
      alignItems={"center"}
      mb={2}
      onClick={() => {
        if (!isDetailPage) {
          handleCheckedOptions(mainIndex, option.type);
        }
      }}
    >
      <CustomCheckBox isChecked={option?.isSelected} />
      <Typography
        variant={option?.title === "Different Address" ? "body1" : "h6"}
        fontWeight={500}
        color={"text.davyGrey"}
      >
        {option?.title === "Different Address"
          ? "Different Address/Company Name"
          : option?.title}
      </Typography>
    </Stack>
  );

  const renderEngineerSiteVisitOptions = (mainIndex, option, cliNumber) => (
    <Stack
      direction={"row"}
      spacing={1}
      alignItems={"center"}
      // mb={2}
      onClick={() => {
        // if (cliNumber === undefined || cliNumber === true) {
        if (!isDetailPage) {
          handleCheckedOptions(mainIndex, option.type);
        }
        // }
      }}
    >
      <CustomCheckBox isChecked={option?.isSelected} />
      <Typography variant="body1" fontWeight={500} color={"text.davyGrey"}>
        {option?.title == "SOGEA - £60" ? "Install" : option?.title}
      </Typography>
    </Stack>
  );

  const renderCliBox = () =>
    cliData?.map((val, mainIndex) => (
      <ConnectionBox
        key={mainIndex}
        valll={val}
        mainIndex={mainIndex}
        handleDisableLine={handleDisableLine}
        handleChangeLineValue={handleChangeLineValue}
        handleChangeCliNumber={handleChangeCliNumber}
        handleChangeAddress={handleChangeAddress}
        handeDeleteCliBox={handeDeleteCliBox}
        handleChangeUpgradeCharge={handleChangeUpgradeCharge}
        handleSelectUpgradeAttachedValue={handleSelectUpgradeAttachedValue}
        renderCliOptions={renderCliOptions}
        renderEngineerSiteVisitOptions={renderEngineerSiteVisitOptions}
        renderCliLines={renderCliLines}
        handleUpdateNumber={handleUpdateNumber}
        handleSelectTimePeriod={handleSelectTimePeriod}
        handleSelectPaymentWauy={handleSelectPaymentWauy}
        handleUpdateSaleType={handleUpdateSaleType}
      />
    ));

  const renderCliLines = (option, mainIndex) => {
    return option?.lineValues?.map((vall, lineIndex) => {
      return (
        <ConnectionLines
          key={lineIndex}
          mainIndex={mainIndex}
          option={option}
          lineIndex={lineIndex}
          vall={vall}
          handleChangeLegacyValue={handleChangeLegacyValue}
          handleEditLegacySalePrice={handleEditLegacySalePrice}
          handleChangeLegacyService={handleChangeLegacyService}
          handleChangeNgnValue={handleChangeNgnValue}
          handleChangeNgnService={handleChangeNgnService}
          legacyTarrifService={legacyTarrifService}
          legacyTarifOptions={legacyTarifOptions}
          digitalNgnTarrifOption={digitalNgnTarrifOption}
          digitalTarrifService={digitalTarrifService}
        />
      );
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await getBroadbandForWeb(uniqueServices);
      if (response.data.status === "success") {
        const data = response.data.data;
        setLegacyTarrifService(data?.legacyService);
        setLegacyTarifOptions(data?.legacyTarrif);
        setDigitalTarrifService(data?.digitalService);
        setdigitalNgnTarrifOption(data?.digitalTarrif);
      }
    } catch (error) {
      console.error(error);
      setError(true);
    } finally {
      setIsLoading(false);
      setError(true);
    }
  };

  return (
    <Box>
      <AppLoader isVisible={isLoading} />
      <Stack spacing={1}>{renderCliBox()}</Stack>

      <Stack mt={2} alignItems={"flex-end"}>
        <GlobalButton
          disabled={cliData?.length === 5 || isDetailPage}
          variant={"custom_gradient"}
          title={"Add new cli number"}
          startIcon={<AddIcon />}
          handleClickBtn={handlAddCliBox}
          style={{ width: 220, borderRadius: "100px" }}
        />
      </Stack>
    </Box>
  );
};

export default memo(Cli);
